import Button from 'components/Form/Button';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import React, { useMemo, useState } from 'react';
import { Icon, Icons } from 'components/Icon';
import { Link } from 'react-router-dom';
import { useGetTrusteeList } from 'common/queries-and-mutations/trustee';
import { Loader } from 'components/Loader';
import AddTrusteesModal from '../../Trustees/AddTrusteesModal';
import withCreatePortal from 'components/HOC/withCreatePortal';
import { getObjectCategories } from 'utils';
import TrusteeBankAssetCard from './TrusteeBankAssetCard';
import TrusteeBenefitAssetCard from './TrusteeBenefitAssetCard';
import TrusteeStockAssetCard from './TrusteeStockAssetCard';
import TrusteePhysicalAssetCard from './TrusteePhysicalAssetCard';
import TrusteeBondAssetCard from './TrusteeBondAssetCard';
import TrusteePensionAssetCard from './TrusteePensionAssetCard';
import AlertNotification from 'components/AlertNotification';
import { useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';
import { STEPS } from 'constants/steps';

const EnhancedAddTrusteesForm = withCreatePortal(AddTrusteesModal);

export default function ManageTrustee() {
  const { user } = useAppSelector(selectAccountDetails);
  const allowPage = useMemo(() => {
    if (user) {
      return JSON.parse(user.planAllowPage);
    }
  }, [user]);

  const [selectedTrusteeId, setSelectedTrusteeId] = useState<number | null>(
    null
  );
  const [showAddModal, setShowAddModal] = useState(false);

  const { data, isLoading, refetch } = useGetTrusteeList({
    enabled: true
  });

  return (
    <div>
      {isLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <Breadcrumb title="Assets" />
            {allowPage.includes(STEPS.TRUSTEE_INFORMATION) && (
              <Button
                variant="outline"
                size="custom"
                className={`mt-1 w-[12rem] rounded border border-[#FF6600] bg-[#FF66001A] py-2.5 font-semibold text-[#FF6600]`}
                label="Create new Trustee"
                onClick={() => setShowAddModal(true)}
              ></Button>
            )}
          </div>
          <div className="mt-5 rounded-lg bg-white p-8">
            <p className="text-lg font-medium text-[#353535]">
              Manage all trustees
            </p>

            {data?.map(trustee => {
              const assignAssets = getObjectCategories(trustee);
              return (
                <div className="flex flex-col" key={trustee.id}>
                  <div className="mb-1 mt-4 rounded-[10px] border border-[#E9EEFF] bg-[#FBFCFF] p-5">
                    <div className="flex flex-wrap items-center justify-between px-4 pb-1">
                      <div className="my-2 mr-2">
                        <div className="inline-flex rounded-xl bg-[#FF66061F] p-0.5 px-3 text-xs  text-[#FF6600]">
                          Trustee{' '}
                          {`${trustee.id! > 9 ? trustee.id : '0' + trustee.id}`}
                        </div>
                        <div className="mt-5 font-circular text-sm font-medium text-slate-600">
                          Name
                        </div>
                        <div className="font-circular text-base font-medium text-slate-600">
                          {`${trustee?.trusteeFirstName ?? ''} ${
                            trustee.trusteeMiddleName ?? ''
                          } ${trustee.trusteeLastName ?? ''}`}
                        </div>
                      </div>
                      <div className="my-2 w-1/5 space-y-2">
                        <div className="inline-flex h-5" />
                        <div className="mt-5 font-circular text-sm font-medium text-slate-600">
                          E-mail address
                        </div>
                        <div className="font-circular text-base font-medium text-slate-600">
                          {trustee.emailAddress}
                        </div>
                      </div>
                      <div className="my-2 mr-2 w-1/5 space-y-2">
                        <div className="inline-flex h-3" />
                        <div className="font-circular text-sm font-medium text-slate-600">
                          Assigned
                        </div>
                        <div className="font-circular text-base font-medium text-slate-600">
                          {assignAssets.length > 0
                            ? assignAssets.join(', ')
                            : 'Not Assigned'}
                        </div>
                      </div>
                      <div className="mr-2 flex flex-col space-y-6">
                        <div
                          role="button"
                          tabIndex={-1}
                          className="flex items-center gap-2"
                          onClick={() =>
                            setSelectedTrusteeId(
                              trustee.id === selectedTrusteeId
                                ? null
                                : trustee.id
                            )
                          }
                        >
                          <p className="text-sm font-medium text-[#FF6600]">
                            Show assigned assets
                          </p>
                          <div
                            className={`${
                              selectedTrusteeId === trustee.id
                                ? 'rotate-180'
                                : ''
                            }`}
                          >
                            <Icon name={Icons.ArrowDownCircle} />
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-end">
                          <Link
                            to={`/dashboard/assets/trustee/assign-trustee/${trustee.id}`}
                          >
                            <Button
                              variant="outline"
                              size="custom"
                              className="w-[9rem] place-self-end rounded bg-[#FF6600] p-3 text-center text-xs font-medium text-[#FFFFFF]"
                              label="Assign new Asset"
                            ></Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  {selectedTrusteeId === trustee.id && (
                    <div>
                      {trustee.banks.map(asset => (
                        <TrusteeBankAssetCard
                          key={asset.id}
                          assetName={asset.bankName}
                          assetId={asset.id!}
                          trusteeName={`${trustee?.trusteeFirstName ?? ''} ${
                            trustee.trusteeMiddleName ?? ''
                          } ${trustee.trusteeLastName ?? ''}`}
                          trusteeId={trustee.id!}
                          onSuccess={() => refetch()}
                        />
                      ))}
                      {trustee.employeeBenefits.map(asset => (
                        <TrusteeBenefitAssetCard
                          key={asset.id}
                          assetName={asset.employeeBenefit}
                          assetId={asset.id!}
                          trusteeName={`${trustee?.trusteeFirstName ?? ''} ${
                            trustee.trusteeMiddleName ?? ''
                          } ${trustee.trusteeLastName ?? ''}`}
                          trusteeId={trustee.id!}
                          onSuccess={() => refetch()}
                        />
                      ))}
                      {trustee.stocks.map(asset => (
                        <TrusteeStockAssetCard
                          key={asset.id}
                          assetName={asset.certificateNo}
                          assetId={asset.id!}
                          trusteeName={`${trustee?.trusteeFirstName ?? ''} ${
                            trustee.trusteeMiddleName ?? ''
                          } ${trustee.trusteeLastName ?? ''}`}
                          trusteeId={trustee.id!}
                          onSuccess={() => refetch()}
                        />
                      ))}
                      {trustee.physicalAssets.map(asset => (
                        <TrusteePhysicalAssetCard
                          key={asset.id}
                          assetName={asset.regNo}
                          assetId={asset.id!}
                          trusteeName={`${trustee?.trusteeFirstName ?? ''} ${
                            trustee.trusteeMiddleName ?? ''
                          } ${trustee.trusteeLastName ?? ''}`}
                          trusteeId={trustee.id!}
                          onSuccess={() => refetch()}
                        />
                      ))}
                      {trustee.bonds.map(asset => (
                        <TrusteeBondAssetCard
                          key={asset.id}
                          assetName={asset.issuerName}
                          assetId={asset.id!}
                          trusteeName={`${trustee?.trusteeFirstName ?? ''} ${
                            trustee.trusteeMiddleName ?? ''
                          } ${trustee.trusteeLastName ?? ''}`}
                          trusteeId={trustee.id!}
                          onSuccess={() => refetch()}
                        />
                      ))}
                      {trustee.pensions.map(asset => (
                        <TrusteePensionAssetCard
                          key={asset.id}
                          assetName={asset.contributorName}
                          assetId={asset.id!}
                          trusteeName={`${trustee?.trusteeFirstName ?? ''} ${
                            trustee.trusteeMiddleName ?? ''
                          } ${trustee.trusteeLastName ?? ''}`}
                          trusteeId={trustee.id!}
                          onSuccess={() => refetch()}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {!data?.length && (
              <AlertNotification
                title="No trustee to show yet"
                type="success"
                message="All your trustee will be displayed here. Additionally, you can utilize the button above to add a new trustee."
                useModern
              />
            )}
          </div>
        </>
      )}
      {showAddModal && (
        <EnhancedAddTrusteesForm
          onClose={() => {
            setShowAddModal(false);
          }}
          onSuccess={() => {
            setShowAddModal(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}
